/**
 * Branch Service - Firestore operations for branch management
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Branch } from '../types';

const COLLECTION = 'branches';

export const branchService = {
  /**
   * Get a branch by ID
   */
  async getById(branchId: string): Promise<Branch | null> {
    const docRef = doc(db, COLLECTION, branchId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { branchId: docSnap.id, ...docSnap.data() } as Branch;
  },

  /**
   * List all branches
   */
  async list(): Promise<Branch[]> {
    const q = query(collection(db, COLLECTION), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      branchId: doc.id,
      ...doc.data(),
    })) as Branch[];
  },

  /**
   * Subscribe to all branches in real-time
   */
  subscribe(callback: (branches: Branch[]) => void): Unsubscribe {
    const q = query(collection(db, COLLECTION), orderBy('name', 'asc'));

    return onSnapshot(
      q,
      (snapshot) => {
        const branches = snapshot.docs.map(doc => ({
          branchId: doc.id,
          ...doc.data(),
        })) as Branch[];
        callback(branches);
      },
      (error) => {
        console.error('Error subscribing to branches:', error);
      }
    );
  },

  /**
   * List branches by franchise
   */
  async listByFranchise(franchiseId: string): Promise<Branch[]> {
    const q = query(
      collection(db, COLLECTION),
      where('franchiseId', '==', franchiseId),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      branchId: doc.id,
      ...doc.data(),
    })) as Branch[];
  },

  /**
   * Subscribe to branches by franchise in real-time
   */
  subscribeByFranchise(
    franchiseId: string,
    callback: (branches: Branch[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, COLLECTION),
      where('franchiseId', '==', franchiseId),
      orderBy('name', 'asc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const branches = snapshot.docs.map(doc => ({
          branchId: doc.id,
          ...doc.data(),
        })) as Branch[];
        callback(branches);
      },
      (error) => {
        console.error('Error subscribing to franchise branches:', error);
      }
    );
  },
};
